import React from "react";
import { Button, Toggle } from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import GuidAccordionCell from "../components/guidAccordionCell/GuidAccordionCell";
import farmIcon from "../assets/icons/farm-kind.svg";
import farmerIcon from "../assets/icons/farmer-kind.svg";
import userIcon from "../assets/icons/user-kind.svg";
import fuelIcon from "../assets/icons/fuel.svg";
import { createCurrencyFormatter } from "../utils/currencyFormatter";
import { normalizeNumber } from "../utils/normalizeNumber";

const getKindIcon = (kind) => {
  switch (kind) {
    case "Farm":
      return farmIcon;
    case "Farmer":
      return farmerIcon;
    case "Agent":
      return userIcon;
    default:
      return null;
  }
};

const COLUMN_ORDER = [
  "kind",
  "guid",
  "client",
  "created",
  "fuel_balance",
  "autoapprove",
  "actions",
];

export const chatRelationsMapper = (
  relations,
  t,
  i18n,
  {
    onFuelEdit,
    onPriceEdit,
    onDelete,
    balanceView,
    onAutoapproveEdit,
    settingsMutation,
  },
) => {
  if (!Array.isArray(relations) || relations.length === 0) {
    return { headers: [], rows: [] };
  }
  const formatter = createCurrencyFormatter(i18n.language);

  const headers = COLUMN_ORDER.map((key) => {
    if (key === "actions") {
      return {
        key,
        header: "",
      };
    }
    return {
      key,
      header: t(`main.chat.chatClientsTable.${key}`),
    };
  });

  const rows = relations.map((item) => {
    const row = {};
    const guid = item.guid;
    const primaryValue =
      balanceView === "fuel" ? item.fuel_balance : item.balance;
    const isNegative = Number(primaryValue) < 0;

    Object.keys(item).forEach((key) => {
      if (key === "guid") {
        row[key] = <GuidAccordionCell item={item} t={t} source="relations" />;
      } else if (key === "created") {
        const date = new Date(item[key]);

        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");

        row[key] = `${yyyy}-${mm}-${dd} ${hh}:${min}`;
      } else if (key === "publicName") {
        row[key] = item[key] ? String(item[key]) : "";
      } else if (key === "kind") {
        const icon = getKindIcon(item.kind);
        row[key] = (
          <span className="kind-cell">
            {icon && (
              <img src={icon} alt={`${item.kind} icon`} className="kind-icon" />
            )}
            {t(`main.chat.chatClientsTable.${item.kind.toLowerCase()}`)}
          </span>
        );
      } else if (key === "cost") {
        row[key] = Number((Number(item[key]) * 100).toPrecision(12)).toString();
      } else if (key === "client") {
        item[key] == null ? (row[key] = "") : (row[key] = String(item[key]));
      } else if (key === "fuel_balance") {
        row[key] = (
          <div className="fuel_cell">
            {balanceView === "fuel" ? (
              <>
                <div
                  className={`fuel_cell_main ${
                    isNegative ? "fuel_cell_main--negative" : ""
                  }`}
                >
                  <img src={fuelIcon} alt="Fuel" className="fuel_cell_img" />
                  {item.fuel_balance}
                </div>

                <div className="fuel_cell_second">
                  <div className="fuel_secondary_item">
                    {t("main.chat.chatClientsTable.cost")}{" "}
                    {Number(
                      (Number(item.cost) * 100).toPrecision(12),
                    ).toString()}
                  </div>
                  <div className="fuel_secondary_item">
                    {formatter.format(
                      normalizeNumber(Number(item.balance) || 0),
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className={`fuel_cell_main ${
                    isNegative ? "fuel_cell_main--negative" : ""
                  }`}
                >
                  {formatter.format(normalizeNumber(Number(item.balance) || 0))}
                </div>

                <div className="fuel_cell_second">
                  <div className="fuel_secondary_item">
                    {t("main.chat.chatClientsTable.cost")}{" "}
                    {Number(
                      (Number(item.cost) * 100).toPrecision(12),
                    ).toString()}
                  </div>
                  <div className="fuel_secondary_item">
                    <img src={fuelIcon} alt="Fuel" className="fuel_cell_img" />
                    {item.fuel_balance}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      } else if (key === "autoapprove") {
        row[key] = (
          <Toggle
            toggled={item[key]}
            id={`toggle-autoapprove-${guid}`}
            labelA={t("main.chat.chatClientsTable.autoapproveOff")}
            labelB={t("main.chat.chatClientsTable.autoapproveOn")}
            labelText=""
            onToggle={(checked) => onAutoapproveEdit(checked, item)}
            disabled={settingsMutation.isPending}
          />
        );
      } else {
        row[key] = String(item[key]) || "";
      }
    });

    row.actions = (
      <div className="actions_buttons">
        <Button
          size="sm"
          kind="tertiary"
          onClick={() => onFuelEdit(item)}
          className="actions_buttons_button"
        >
          {t("main.admin.top_up_fuel.title")}
        </Button>
        <Button
          size="sm"
          kind="tertiary"
          onClick={() => onPriceEdit(item)}
          className="actions_buttons_button"
        >
          {t("main.chat.chatClientsTable.editDescription")}
        </Button>
        <Button
          hasIconOnly
          renderIcon={TrashCan}
          kind="tertiary"
          size="sm"
          iconDescription={t("main.chat.chatClientsTable.deleteDesc")}
          onClick={() => onDelete(item)}
        />
      </div>
    );

    row.id = item.id ?? guid;
    return row;
  });

  return { headers, rows };
};
